import React from 'react';
import { arrayOf, oneOfType, bool, func } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { propTypes } from '../../util/types';
import { useIntl, intlShape } from '../../util/reactIntl';
import { fetchTopbarListings } from '../../ducks/search.duck';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { useConfiguration } from '../../context/configurationContext';
import { getUsersById } from '../../ducks/marketplaceData.duck';

import { Page, LayoutSingleColumn } from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import TopbarNavigation from '../../containers/TopbarContainer/Topbar/TopbarNavigation/TopbarNavigation';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

import SectionHero from './SectionHero/SectionHero';
import SectionHowItWorks from './SectionHowItWorks/SectionHowItWorks';
import SectionPayment from './SectionPayment/SectionPayment';
import SectionSearchByCategories from './SectionSearchByCategories/SectionSearchByCategories';
import SectionSearchBySellers from './SectionSearchBySellers/SectionSearchBySellers';
import SectionSearchByBrands from './SectionSearchByBrands/SectionSearchByBrands';

import css from './LandingPage.module.css';

const LandingPageComponent = props => {
  const {
    scrollingDisabled,
    intl,
    config,
    users,
    fetchUsersInProgress,
    fetchUsersError,
    onFetchTopbarListings,
    fetchTopbarListingsInProgress,
    fetchTopbarListingsError,
    topbarListings,
    totalListingsCount,
  } = props;

  const title = intl.formatMessage(
    {
      id: 'LandingPage.title',
    },
    { marketplaceName: config.marketplaceName }
  );

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn
        topbar={<TopbarContainer />}
        topbarNavigation={<TopbarNavigation />}
        footer={<FooterContainer />}
      >
        <div className={css.root}>
          <div className={css.content}>
            <SectionHero
              onFetchTopbarListings={onFetchTopbarListings}
              fetchTopbarListingsInProgress={fetchTopbarListingsInProgress}
              fetchTopbarListingsError={fetchTopbarListingsError}
              topbarListings={topbarListings}
              totalListingsCount={totalListingsCount}
            />
            <div className={css.sectionContent}>
              <SectionHowItWorks intl={intl} />
            </div>
            <div className={css.sectionContentFullWidth}>
              <div className={css.sectionContentFullWidthWrapper}>
                <SectionPayment intl={intl} />
              </div>
            </div>
            <div className={css.sectionContent}>
              <SectionSearchByCategories />
            </div>
            <div className={css.sectionContent}>
              <SectionSearchBySellers
                users={users}
                fetchUsersInProgress={fetchUsersInProgress}
                fetchUsersError={fetchUsersError}
              />
            </div>
            <div className={css.sectionContent}>
              <SectionSearchByBrands />
            </div>
            <div className={css.sectionContentFullWidth}>
              <div className={css.sectionContentFullWidthWrapper}>
                <SectionPayment intl={intl} />
              </div>
            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

LandingPageComponent.defaultProps = {
  fetchUsersInProgress: false,
  fetchUsersError: null,
  fetchTopbarListingsInProgress: false,
  fetchTopbarListingsError: null,
};

LandingPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,

  users: arrayOf(oneOfType([propTypes.user, propTypes.currentUser])),
  fetchUsersInProgress: bool.isRequired,
  fetchUsersError: propTypes.error,

  onFetchTopbarListings: func.isRequired,
  fetchTopbarListingsInProgress: bool.isRequired,
  fetchTopbarListingsError: propTypes.error,
  topbarListings: arrayOf(propTypes.listing),

  // from useIntl
  intl: intlShape.isRequired,
};

const EnhancedLandingPage = props => {
  const intl = useIntl();
  const config = useConfiguration();

  return <LandingPageComponent intl={intl} config={config} {...props} />;
};

const mapStateToProps = state => {
  const { userIds, fetchUsersInProgress, fetchUsersError } = state.LandingPage;
  const { fetchTopbarListingsInProgress, fetchTopbarListingsError, topbarListings, totalListingsCount } = state.search;
  const users = getUsersById(state, userIds);
  return {
    scrollingDisabled: isScrollingDisabled(state),
    users,
    fetchUsersInProgress,
    fetchUsersError,
    fetchTopbarListingsInProgress,
    fetchTopbarListingsError,
    topbarListings,
    totalListingsCount,
  };
};

const mapDispatchToProps = dispatch => ({
  onFetchTopbarListings: keywords => dispatch(fetchTopbarListings(keywords)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedLandingPage);

export default LandingPage;
